import React, { useEffect, useState, useCallback } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import FormInput from '../../components/forms/FormInput';
import FormSelect from '../../components/forms/FormSelect';
import FormTextarea from '../../components/forms/FormTextarea';
import {
  getCurrentlyInside,
  getPreRegistered,
  getVisitorHistory,
  getVisitorReports,
  createVisitor,
  checkInVisitor,
  checkOutVisitor,
  approveVisitor,
  rejectVisitor,
  deleteVisitor,
} from '../../api/visitors';
import { getApartments } from '../../api/gasReadings';

const visitorTypes = [
  { value: 'guest', label: 'Guest' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'service', label: 'Service' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'cab', label: 'Cab' },
  { value: 'other', label: 'Other' },
];

const idProofTypes = [
  { value: 'aadhar', label: 'Aadhar' },
  { value: 'driving_license', label: 'Driving License' },
  { value: 'passport', label: 'Passport' },
  { value: 'voter_id', label: 'Voter ID' },
  { value: 'pan_card', label: 'PAN Card' },
  { value: 'other', label: 'Other' },
];

const statusColors = {
  pre_registered: { bg: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6' },
  approved: { bg: 'rgba(16, 185, 129, 0.1)', color: '#10B981' },
  rejected: { bg: 'rgba(239, 68, 68, 0.1)', color: '#EF4444' },
  checked_in: { bg: 'rgba(16, 185, 129, 0.15)', color: '#059669' },
  checked_out: { bg: 'rgba(107, 114, 128, 0.1)', color: '#6B7280' },
  cancelled: { bg: 'rgba(239, 68, 68, 0.1)', color: '#EF4444' },
};

const typeEmojis = {
  guest: '👤', delivery: '📦', service: '🔧', vendor: '🏢', cab: '🚕', other: '📋',
};

function StatusBadge({ status }) {
  const style = statusColors[status] || statusColors.cancelled;
  const label = (status || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-medium"
      style={{ background: style.bg, color: style.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: style.color }} />
      {label}
    </span>
  );
}

function LiveDuration({ checkedInAt }) {
  const [display, setDisplay] = useState('');
  useEffect(() => {
    if (!checkedInAt) return;
    const update = () => {
      const diff = Math.floor((Date.now() - new Date(checkedInAt).getTime()) / 60000);
      const h = Math.floor(diff / 60);
      const m = diff % 60;
      setDisplay(h > 0 ? `${h}h ${m}m` : `${m}m`);
    };
    update();
    const timer = setInterval(update, 60000);
    return () => clearInterval(timer);
  }, [checkedInAt]);
  return <span className="text-xs font-medium" style={{ color: '#059669' }}>{display}</span>;
}

const emptyForm = {
  name: '', phone: '', type: 'guest', purpose: '', company_name: '',
  apartment_id: '', host_name: '', vehicle_number: '', vehicle_type: '',
  id_proof_type: '', id_proof_number: '', expected_at: '', notes: '',
};

export default function VisitorsPage() {
  const [activeTab, setActiveTab] = useState('current');
  const [loading, setLoading] = useState(true);
  const [currentVisitors, setCurrentVisitors] = useState([]);
  const [preRegistered, setPreRegistered] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyMeta, setHistoryMeta] = useState({ current_page: 1, last_page: 1 });
  const [reports, setReports] = useState(null);
  const [apartments, setApartments] = useState([]);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showCheckOutModal, setShowCheckOutModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [form, setForm] = useState({ ...emptyForm });
  const [checkInForm, setCheckInForm] = useState({ badge_number: '', notes: '' });
  const [checkOutForm, setCheckOutForm] = useState({ notes: '' });

  // Filters
  const [historySearch, setHistorySearch] = useState('');
  const [historyType, setHistoryType] = useState('');
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');

  const fetchApartments = useCallback(async () => {
    try {
      const res = await getApartments({ all: true });
      const data = res.data || res;
      setApartments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load apartments', err);
    }
  }, []);

  const fetchCurrent = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCurrentlyInside();
      setCurrentVisitors(res.data || []);
    } catch (err) {
      console.error('Failed to load current visitors', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPreRegistered = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPreRegistered();
      setPreRegistered(res.data || []);
    } catch (err) {
      console.error('Failed to load pre-registered visitors', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await getVisitorHistory({
        page, search: historySearch || undefined,
        type: historyType || undefined,
        date_from: historyDateFrom || undefined,
        date_to: historyDateTo || undefined,
      });
      setHistory(res.data || []);
      setHistoryMeta(res.meta || { current_page: 1, last_page: 1 });
    } catch (err) {
      console.error('Failed to load visitor history', err);
    } finally {
      setLoading(false);
    }
  }, [historySearch, historyType, historyDateFrom, historyDateTo]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getVisitorReports();
      setReports(res);
    } catch (err) {
      console.error('Failed to load reports', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApartments();
  }, [fetchApartments]);

  useEffect(() => {
    if (activeTab === 'current') fetchCurrent();
    else if (activeTab === 'pre-registered') fetchPreRegistered();
    else if (activeTab === 'history') fetchHistory();
    else if (activeTab === 'reports') fetchReports();
  }, [activeTab, fetchCurrent, fetchPreRegistered, fetchHistory, fetchReports]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAddVisitor = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === '') delete payload[k]; });
      await createVisitor(payload);
      setShowAddModal(false);
      setForm({ ...emptyForm });
      if (activeTab === 'current') fetchCurrent();
      else if (activeTab === 'pre-registered') fetchPreRegistered();
    } catch (err) {
      console.error('Failed to create visitor', err);
      alert(err.response?.data?.message || 'Failed to create visitor');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckIn = async () => {
    if (!selectedVisitor) return;
    setSubmitting(true);
    try {
      await checkInVisitor(selectedVisitor.id, checkInForm);
      setShowCheckInModal(false);
      setCheckInForm({ badge_number: '', notes: '' });
      setSelectedVisitor(null);
      if (activeTab === 'pre-registered') fetchPreRegistered();
      else fetchCurrent();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to check in visitor');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckOut = async () => {
    if (!selectedVisitor) return;
    setSubmitting(true);
    try {
      await checkOutVisitor(selectedVisitor.id, checkOutForm);
      setShowCheckOutModal(false);
      setCheckOutForm({ notes: '' });
      setSelectedVisitor(null);
      fetchCurrent();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to check out visitor');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (visitor) => {
    try {
      await approveVisitor(visitor.id);
      fetchPreRegistered();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to approve visitor');
    }
  };

  const handleReject = async (visitor) => {
    if (!window.confirm(`Reject visitor "${visitor.name}"?`)) return;
    try {
      await rejectVisitor(visitor.id, {});
      fetchPreRegistered();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reject visitor');
    }
  };

  const handleDelete = async (visitor) => {
    if (!window.confirm(`Delete visitor "${visitor.name}"?`)) return;
    try {
      await deleteVisitor(visitor.id);
      if (activeTab === 'pre-registered') fetchPreRegistered();
      else if (activeTab === 'history') fetchHistory();
      else fetchCurrent();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete visitor');
    }
  };

  const openCheckIn = (visitor) => {
    setSelectedVisitor(visitor);
    setCheckInForm({ badge_number: '', notes: '' });
    setShowCheckInModal(true);
  };

  const openCheckOut = (visitor) => {
    setSelectedVisitor(visitor);
    setCheckOutForm({ notes: '' });
    setShowCheckOutModal(true);
  };

  const openDetail = (visitor) => {
    setSelectedVisitor(visitor);
    setShowDetailModal(true);
  };

  const tabs = [
    { key: 'current', label: 'Current Visitors' },
    { key: 'pre-registered', label: 'Pre-Registered' },
    { key: 'history', label: 'History' },
    { key: 'reports', label: 'Reports' },
  ];

  const apartmentOptions = apartments.map((a) => ({
    value: a.id, label: `${a.tower} - ${a.unit}${a.owner_name ? ` (${a.owner_name})` : ''}`,
  }));

  const formatDateTime = (dt) => {
    if (!dt) return '-';
    return new Date(dt).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <AppLayout title="Visitor Management">
      <div style={{ padding: '24px' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold" style={{ color: '#1A1A1A' }}>Visitor Management</h1>
            <p className="text-sm mt-1" style={{ color: '#666' }}>Track and manage all visitors to the complex</p>
          </div>
          <Button onClick={() => setShowAddModal(true)}>+ Add Visitor</Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 p-1 rounded-xl" style={{ background: '#F0F0EB' }}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all"
              style={activeTab === tab.key
                ? { background: 'white', color: '#043959', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }
                : { color: '#666' }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-gray-400 text-sm">Loading...</div>
          </div>
        ) : (
          <>
            {/* Current Visitors Tab */}
            {activeTab === 'current' && (
              <div>
                {currentVisitors.length === 0 ? (
                  <div className="text-center py-16">
                    <p className="text-4xl mb-3">🏢</p>
                    <p className="text-sm font-medium" style={{ color: '#666' }}>No visitors currently inside</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {currentVisitors.map((v) => (
                      <div key={v.id} className="rounded-xl p-5 flex items-center gap-4"
                        style={{ background: 'white', border: '1px solid #E5E5E0' }}>
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                          style={{ background: '#F0F0EB' }}>
                          {typeEmojis[v.type] || '📋'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-sm" style={{ color: '#1A1A1A' }}>{v.name}</p>
                            <StatusBadge status={v.status} />
                          </div>
                          <p className="text-xs mt-1" style={{ color: '#666' }}>
                            {v.type?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                            {v.purpose ? ` — ${v.purpose}` : ''}
                            {v.apartment ? ` — ${v.apartment.tower}-${v.apartment.unit}` : ''}
                          </p>
                          {v.vehicle_number && (
                            <p className="text-xs mt-0.5" style={{ color: '#999' }}>Vehicle: {v.vehicle_number}</p>
                          )}
                          {v.creator?.name && (
                            <p className="text-xs mt-0.5" style={{ color: '#999' }}>Registered by: {v.creator.name}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <LiveDuration checkedInAt={v.checked_in_at} />
                          {v.badge_number && (
                            <p className="text-xs mt-1" style={{ color: '#999' }}>Badge: {v.badge_number}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="secondary" onClick={() => openDetail(v)}>View</Button>
                          <Button size="sm" variant="danger" onClick={() => openCheckOut(v)}>Check Out</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Pre-Registered Tab */}
            {activeTab === 'pre-registered' && (
              <div>
                {preRegistered.length === 0 ? (
                  <div className="text-center py-16">
                    <p className="text-4xl mb-3">📋</p>
                    <p className="text-sm font-medium" style={{ color: '#666' }}>No pre-registered visitors</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {preRegistered.map((v) => (
                      <div key={v.id} className="rounded-xl p-5 flex items-center gap-4"
                        style={{ background: 'white', border: '1px solid #E5E5E0' }}>
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                          style={{ background: '#F0F0EB' }}>
                          {typeEmojis[v.type] || '📋'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-sm" style={{ color: '#1A1A1A' }}>{v.name}</p>
                            <StatusBadge status={v.status} />
                          </div>
                          <p className="text-xs mt-1" style={{ color: '#666' }}>
                            {v.type?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                            {v.purpose ? ` — ${v.purpose}` : ''}
                            {v.apartment ? ` — ${v.apartment.tower}-${v.apartment.unit}` : ''}
                          </p>
                          {v.expected_at && (
                            <p className="text-xs mt-0.5" style={{ color: '#999' }}>Expected: {formatDateTime(v.expected_at)}</p>
                          )}
                          {v.creator?.name && (
                            <p className="text-xs mt-0.5" style={{ color: '#999' }}>Registered by: {v.creator.name}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="secondary" onClick={() => openDetail(v)}>View</Button>
                          {v.status === 'pre_registered' && (
                            <Button size="sm" variant="ghost" onClick={() => handleApprove(v)}
                              style={{ color: '#10B981' }}>Approve</Button>
                          )}
                          {(v.status === 'pre_registered' || v.status === 'approved') && (
                            <>
                              <Button size="sm" onClick={() => openCheckIn(v)}>Check In</Button>
                              <Button size="sm" variant="ghost" onClick={() => handleReject(v)}
                                style={{ color: '#EF4444' }}>Reject</Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <FormInput placeholder="Search name, phone, vehicle..."
                    value={historySearch} onChange={(e) => setHistorySearch(e.target.value)} />
                  <FormSelect options={visitorTypes} value={historyType}
                    onChange={(e) => setHistoryType(e.target.value)} />
                  <FormInput type="date" value={historyDateFrom}
                    onChange={(e) => setHistoryDateFrom(e.target.value)} />
                  <FormInput type="date" value={historyDateTo}
                    onChange={(e) => setHistoryDateTo(e.target.value)} />
                </div>
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #E5E5E0' }}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ background: '#F9F9F6' }}>
                        <th className="text-left px-4 py-3 font-medium" style={{ color: '#666' }}>Visitor</th>
                        <th className="text-left px-4 py-3 font-medium" style={{ color: '#666' }}>Type</th>
                        <th className="text-left px-4 py-3 font-medium" style={{ color: '#666' }}>Apartment</th>
                        <th className="text-left px-4 py-3 font-medium" style={{ color: '#666' }}>Check In</th>
                        <th className="text-left px-4 py-3 font-medium" style={{ color: '#666' }}>Check Out</th>
                        <th className="text-left px-4 py-3 font-medium" style={{ color: '#666' }}>Duration</th>
                        <th className="text-left px-4 py-3 font-medium" style={{ color: '#666' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.length === 0 ? (
                        <tr><td colSpan={7} className="text-center py-8" style={{ color: '#999' }}>No records found</td></tr>
                      ) : history.map((v) => (
                        <tr key={v.id} className="border-t cursor-pointer hover:bg-gray-50"
                          style={{ borderColor: '#E5E5E0' }}
                          onClick={() => openDetail(v)}>
                          <td className="px-4 py-3">
                            <p className="font-medium" style={{ color: '#1A1A1A' }}>{v.name}</p>
                            {v.phone && <p className="text-xs" style={{ color: '#999' }}>{v.phone}</p>}
                          </td>
                          <td className="px-4 py-3">
                            <span>{typeEmojis[v.type] || ''} {v.type?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</span>
                          </td>
                          <td className="px-4 py-3" style={{ color: '#666' }}>
                            {v.apartment ? `${v.apartment.tower}-${v.apartment.unit}` : '-'}
                          </td>
                          <td className="px-4 py-3" style={{ color: '#666' }}>{formatDateTime(v.checked_in_at)}</td>
                          <td className="px-4 py-3" style={{ color: '#666' }}>{formatDateTime(v.checked_out_at)}</td>
                          <td className="px-4 py-3" style={{ color: '#666' }}>{v.duration || '-'}</td>
                          <td className="px-4 py-3"><StatusBadge status={v.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {historyMeta.last_page > 1 && (
                  <div className="flex justify-center gap-2 mt-4">
                    {Array.from({ length: historyMeta.last_page }, (_, i) => i + 1).map((page) => (
                      <button key={page} onClick={() => fetchHistory(page)}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium"
                        style={page === historyMeta.current_page
                          ? { background: '#043959', color: 'white' }
                          : { background: '#F0F0EB', color: '#666' }}>
                        {page}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Reports Tab */}
            {activeTab === 'reports' && reports && (
              <div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: 'Total Today', value: reports.total_today, emoji: '📊' },
                    { label: 'Currently Inside', value: reports.currently_inside, emoji: '🏢' },
                    { label: 'Pre-Registered', value: reports.pre_registered, emoji: '📋' },
                    { label: 'Avg Duration', value: `${reports.avg_duration_minutes}m`, emoji: '⏱️' },
                  ].map((stat) => (
                    <div key={stat.label} className="rounded-xl p-5"
                      style={{ background: 'white', border: '1px solid #E5E5E0' }}>
                      <p className="text-2xl mb-2">{stat.emoji}</p>
                      <p className="text-2xl font-bold" style={{ color: '#043959' }}>{stat.value}</p>
                      <p className="text-xs mt-1" style={{ color: '#666' }}>{stat.label}</p>
                    </div>
                  ))}
                </div>
                {reports.by_type && Object.keys(reports.by_type).length > 0 && (
                  <div className="rounded-xl p-5" style={{ background: 'white', border: '1px solid #E5E5E0' }}>
                    <h3 className="font-semibold text-sm mb-4" style={{ color: '#1A1A1A' }}>Visitors by Type</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {Object.entries(reports.by_type).map(([type, count]) => (
                        <div key={type} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: '#F9F9F6' }}>
                          <span className="text-xl">{typeEmojis[type] || '📋'}</span>
                          <div>
                            <p className="font-semibold text-sm" style={{ color: '#1A1A1A' }}>{count}</p>
                            <p className="text-xs" style={{ color: '#666' }}>{type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Add Visitor Modal */}
        <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add Visitor" size="2xl">
          <form onSubmit={handleAddVisitor}>
            <div className="grid grid-cols-2 gap-4">
              <FormInput label="Name *" name="name" value={form.name} onChange={handleChange} required />
              <FormInput label="Phone" name="phone" value={form.phone} onChange={handleChange} />
              <FormSelect label="Visitor Type *" name="type" options={visitorTypes}
                value={form.type} onChange={handleChange} />
              <FormInput label="Purpose" name="purpose" value={form.purpose} onChange={handleChange} />
              <FormInput label="Company Name" name="company_name" value={form.company_name} onChange={handleChange} />
              <FormSelect label="Apartment" name="apartment_id" options={apartmentOptions}
                value={form.apartment_id} onChange={handleChange} />
              <FormInput label="Host Name" name="host_name" value={form.host_name} onChange={handleChange} />
              <FormInput label="Vehicle Number" name="vehicle_number" value={form.vehicle_number} onChange={handleChange} />
              <FormInput label="Vehicle Type" name="vehicle_type" value={form.vehicle_type} onChange={handleChange} />
              <FormSelect label="ID Proof Type" name="id_proof_type" options={idProofTypes}
                value={form.id_proof_type} onChange={handleChange} />
              <FormInput label="ID Proof Number" name="id_proof_number" value={form.id_proof_number} onChange={handleChange} />
              <FormInput label="Expected At" name="expected_at" type="datetime-local"
                value={form.expected_at} onChange={handleChange} />
            </div>
            <FormTextarea label="Notes" name="notes" value={form.notes} onChange={handleChange} className="mt-4" />
            <div className="flex justify-end gap-3 mt-6">
              <Button type="button" variant="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button type="submit" loading={submitting}>Add Visitor</Button>
            </div>
          </form>
        </Modal>

        {/* Check-In Modal */}
        <Modal isOpen={showCheckInModal} onClose={() => setShowCheckInModal(false)} title="Check In Visitor" size="md">
          <div>
            <p className="text-sm mb-4" style={{ color: '#666' }}>
              Check in <strong>{selectedVisitor?.name}</strong> ({selectedVisitor?.type?.replace(/_/g, ' ')})
            </p>
            <FormInput label="Badge Number" value={checkInForm.badge_number}
              onChange={(e) => setCheckInForm({ ...checkInForm, badge_number: e.target.value })}
              placeholder="e.g. V-001" />
            <FormTextarea label="Notes" value={checkInForm.notes}
              onChange={(e) => setCheckInForm({ ...checkInForm, notes: e.target.value })}
              className="mt-3" rows={2} />
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="secondary" onClick={() => setShowCheckInModal(false)}>Cancel</Button>
              <Button loading={submitting} onClick={handleCheckIn}>Confirm Check In</Button>
            </div>
          </div>
        </Modal>

        {/* Check-Out Modal */}
        <Modal isOpen={showCheckOutModal} onClose={() => setShowCheckOutModal(false)} title="Check Out Visitor" size="md">
          <div>
            <p className="text-sm mb-4" style={{ color: '#666' }}>
              Check out <strong>{selectedVisitor?.name}</strong>?
              {selectedVisitor?.checked_in_at && (
                <span> (Inside for <LiveDuration checkedInAt={selectedVisitor.checked_in_at} />)</span>
              )}
            </p>
            <FormTextarea label="Notes (optional)" value={checkOutForm.notes}
              onChange={(e) => setCheckOutForm({ ...checkOutForm, notes: e.target.value })}
              rows={2} />
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="secondary" onClick={() => setShowCheckOutModal(false)}>Cancel</Button>
              <Button variant="danger" loading={submitting} onClick={handleCheckOut}>Confirm Check Out</Button>
            </div>
          </div>
        </Modal>

        {/* Visitor Detail Modal */}
        <Modal isOpen={showDetailModal} onClose={() => { setShowDetailModal(false); setSelectedVisitor(null); }}
          title="Visitor Details" size="lg">
          {selectedVisitor && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl"
                  style={{ background: '#F0F0EB' }}>
                  {typeEmojis[selectedVisitor.type] || '📋'}
                </div>
                <div>
                  <h3 className="font-semibold text-lg" style={{ color: '#1A1A1A' }}>{selectedVisitor.name}</h3>
                  <StatusBadge status={selectedVisitor.status} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  ['Phone', selectedVisitor.phone],
                  ['Type', selectedVisitor.type?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())],
                  ['Purpose', selectedVisitor.purpose],
                  ['Company', selectedVisitor.company_name],
                  ['Apartment', selectedVisitor.apartment ? `${selectedVisitor.apartment.tower}-${selectedVisitor.apartment.unit}` : null],
                  ['Host', selectedVisitor.host_name || selectedVisitor.host_user?.name],
                  ['Vehicle', selectedVisitor.vehicle_number ? `${selectedVisitor.vehicle_number}${selectedVisitor.vehicle_type ? ` (${selectedVisitor.vehicle_type})` : ''}` : null],
                  ['ID Proof', selectedVisitor.id_proof_type ? `${selectedVisitor.id_proof_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} - ${selectedVisitor.id_proof_number || ''}` : null],
                  ['Badge', selectedVisitor.badge_number],
                  ['Expected At', formatDateTime(selectedVisitor.expected_at)],
                  ['Checked In', formatDateTime(selectedVisitor.checked_in_at)],
                  ['Checked Out', formatDateTime(selectedVisitor.checked_out_at)],
                  ['Duration', selectedVisitor.duration],
                  ['Registered By', selectedVisitor.creator?.name],
                  ['Notes', selectedVisitor.notes],
                ].filter(([, val]) => val && val !== '-').map(([label, val]) => (
                  <div key={label}>
                    <p className="text-xs font-medium mb-0.5" style={{ color: '#999' }}>{label}</p>
                    <p style={{ color: '#1A1A1A' }}>{val}</p>
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
                {selectedVisitor.is_currently_inside && (
                  <Button size="sm" variant="danger" onClick={() => { setShowDetailModal(false); openCheckOut(selectedVisitor); }}>
                    Check Out
                  </Button>
                )}
                {(selectedVisitor.status === 'pre_registered' || selectedVisitor.status === 'approved') && (
                  <Button size="sm" onClick={() => { setShowDetailModal(false); openCheckIn(selectedVisitor); }}>
                    Check In
                  </Button>
                )}
                {!selectedVisitor.is_currently_inside && selectedVisitor.status !== 'checked_out' && (
                  <Button size="sm" variant="ghost" onClick={() => { setShowDetailModal(false); handleDelete(selectedVisitor); }}
                    style={{ color: '#EF4444' }}>Delete</Button>
                )}
              </div>
            </div>
          )}
        </Modal>
      </div>
    </AppLayout>
  );
}
